import { useState } from "react";
import { Loader2, ShieldCheck, ShieldOff, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { tr } from "@/lib/i18n";

function formatError(detail) {
  if (!detail) return tr("Something went wrong. Please try again.");
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function TwoFactorSettings() {
  const { user, refresh } = useAuth();
  const enabled = !!user?.two_factor_enabled;

  const [setup, setSetup] = useState(null); // { secret, qr_data_url }
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const startSetup = async () => {
    setBusy(true);
    try {
      const res = await api.auth2faSetup();
      setSetup(res);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const confirmEnable = async () => {
    if (code.length !== 6) return;
    setBusy(true);
    try {
      await api.auth2faEnable(code.trim());
      toast.success(tr("Two-factor authentication enabled"));
      setSetup(null);
      setCode("");
      await refresh();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    if (!password) return;
    setBusy(true);
    try {
      await api.auth2faDisable(password);
      toast.success(tr("Two-factor authentication disabled"));
      setPassword("");
      await refresh();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const copySecret = async () => {
    if (!setup?.secret) return;
    try {
      await navigator.clipboard.writeText(setup.secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };

  if (enabled) {
    return (
      <div className="space-y-4" data-testid="2fa-enabled-block">
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-green-600 mt-0.5" />
          <div className="text-sm">
            <div className="font-medium">{tr("Two-factor authentication is enabled.")}</div>
            <div className="text-[var(--ink-muted)] text-xs mt-1">{tr("You'll be asked for a 6-digit code from your authenticator app on every sign in.")}</div>
          </div>
        </div>

        <div className="border-t border-[var(--border)] pt-4">
          <label className="overline text-[var(--ink-muted)] block mb-2">{tr("To disable, confirm your password")}</label>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              data-testid="2fa-disable-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={tr("Password")}
              className="flex-1 border border-[var(--border)] px-3 py-2 text-sm focus:outline-none focus:border-[var(--accent)]"
            />
            <button
              data-testid="2fa-disable-btn"
              onClick={disable}
              disabled={busy || !password}
              className="bg-red-600 text-white px-4 py-2 text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              <ShieldOff className="w-4 h-4" />{tr("Disable 2FA")}</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="2fa-disabled-block">
      <div className="flex items-start gap-3">
        <ShieldOff className="w-5 h-5 text-[var(--ink-muted)] mt-0.5" />
        <div className="text-sm">
          <div className="font-medium">{tr("Two-factor authentication is off.")}</div>
          <div className="text-[var(--ink-muted)] text-xs mt-1">{tr("Adding an authenticator app (Google Authenticator, Authy, 1Password, etc.) requires a 6-digit code in addition to your password.")}</div>
        </div>
      </div>

      {!setup ? (
        <button
          data-testid="2fa-setup-btn"
          onClick={startSetup}
          disabled={busy}
          className="bg-[var(--slate-900)] text-white px-4 py-2 text-sm font-medium disabled:opacity-50 flex items-center gap-2"
        >
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          <ShieldCheck className="w-4 h-4" />{tr("Set up authenticator app")}</button>
      ) : (
        <div className="border-t border-[var(--border)] pt-4 space-y-4">
          <div className="text-sm">
            <div className="font-medium mb-1">{tr("1. Scan this QR code")}</div>
            <div className="text-[var(--ink-muted)] text-xs">{tr("Open your authenticator app and scan the code. If you can't scan, enter the secret manually.")}</div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 items-start">
            <img
              data-testid="2fa-qr-image"
              src={setup.qr_data_url}
              alt={tr("TOTP QR code")}
              className="w-44 h-44 border border-[var(--border)] bg-white p-2"
            />
            <div className="flex-1 w-full">
              <label className="overline text-[var(--ink-muted)] block mb-1">{tr("Manual entry secret")}</label>
              <div className="flex gap-2">
                <code
                  data-testid="2fa-secret"
                  className="flex-1 bg-[var(--bg)] border border-[var(--border)] px-2 py-2 text-xs font-mono break-all"
                >
                  {setup.secret}
                </code>
                <button
                  onClick={copySecret}
                  className="border border-[var(--border)] px-2 hover:bg-[var(--secondary)]"
                  title={tr("Copy")}
                >
                  {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>

          <div>
            <div className="font-medium text-sm mb-2">{tr("2. Enter the 6-digit code shown in the app")}</div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                data-testid="2fa-verify-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
                className="flex-1 border border-[var(--border)] px-3 py-2 text-center tracking-[0.3em] font-mono text-lg focus:outline-none focus:border-[var(--accent)]"
              />
              <button
                data-testid="2fa-enable-btn"
                onClick={confirmEnable}
                disabled={busy || code.length !== 6}
                className="bg-[var(--slate-900)] text-white px-4 py-2 text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}{tr("Verify & enable")}</button>
            </div>
          </div>

          <button
            onClick={() => {
              setSetup(null);
              setCode("");
            }}
            className="text-xs text-[var(--ink-muted)] hover:text-[var(--ink)]"
          >{tr("Cancel")}</button>
        </div>
      )}
    </div>
  );
}
